import React from 'react';

import { Container, Link, Typography } from '@mui/material';
import FaqQuestion from '../../Components/FaqQuestion';
import intersectingImage from '../../Assets/intersecting-polygons.png';
import { Res } from '../../resources';

const FaqPage = () => {
  return (
    <Container sx={{ paddingTop: 4, textAlign: 'center' }}>
      <Typography variant="h3" component="h1" gutterBottom sx={{ paddingBottom: 2 }}>
        FAQs
      </Typography>
      <Typography variant="subtitle1" component="h3" gutterBottom sx={{ paddingBottom: 4 }}>
        Looking for help? Below are answers to frequently asked questions about how to use the platform and
        interpret the data we provide
      </Typography>

      <Typography variant="h3" component="h2" gutterBottom sx={{ paddingBottom: 2 }} id="Survey">
        Getting Started
      </Typography>
      <FaqQuestion
        question="What is the Gentian Online Platform (GOP)?"
        answer="The Gentian Online Platform (GOP) is a digital interface designed to streamline the purchase and delivery of ecological site assessments. It allows users to draw or upload an Area of Interest (AOI), purchase instant habitat mapping surveys across the UK, and request bespoke or international surveys. All your purchased and pending surveys can be managed in one convenient location under My Projects."
      />
      <FaqQuestion
        question="How does the platform work?"
        component={
          <React.Fragment>
            <Typography sx={{ color: 'var(--gentian-black)' }} align="left">
              <ol>
                <li>
                  <b>Upload or draw your AOI</b>: Use the ‘Request a Survey’ tool to define your area of
                  interest. Need help? Click the <b>“?”</b> icon to access a quick tutorial on how to use this
                  page.
                </li>
                <li>
                  <b>Choose your product</b>: Select True Sight for an instant habitat survey (for UK
                  locations). Select True Sage for more advanced or international surveys and True Oracle for
                  a bespoke survey. Click <Link href={Res.website_links.TRUE_SIGHT_DETAILS}>here</Link> to
                  learn more about True Sight, <Link href={Res.website_links.TRUE_SAGE_DETAILS}>here</Link> to
                  learn more about True Sage and{' '}
                  <Link href={Res.website_links.TRUE_ORACLE_DETAILS}>here</Link> to learn more about True
                  Oracle.
                </li>
                <li>
                  <b>View your results</b>: Completed surveys are stored in ‘My Projects’ for easy access and
                  download.
                </li>
              </ol>
            </Typography>
          </React.Fragment>
        }
      />

      <Typography
        variant="h3"
        component="h2"
        gutterBottom
        sx={{ paddingBottom: 2, paddingTop: 4 }}
        id="Survey"
      >
        Survey Data & Methods
      </Typography>
      <FaqQuestion
        question="How quickly can I receive a survey?"
        answer="For True Sight, results are typically available in 5 minutes for sites under 40 ha, one hour for sites 40-100 ha, and 24 hours for larger sites. You will receive an email notification when the results are ready. <br/> For True Sage and True Oracle (our quote-based services), our team will contact you as soon as possible after your request. Once the project scope is agreed upon, survey results will be delivered through the platform within 10 working days."
      />
      <FaqQuestion
        question="How accurate is the data provided?"
        component={
          <Typography sx={{ color: 'var(--gentian-black)' }} align="left">
            Our habitat mapping is powered by advanced AI models trained on extensive ecological datasets.
            While these tools are not a substitute for full field surveys, they provide a reliable preliminary
            overview that helps save time and resources.
            <ul>
              <li>
                <b>True Sight</b> identifies 10+ of the most common UK habitat types, as well as priority
                habitats. It performs best in areas with common habitat types, and less so in areas with rare
                or unusual habitat classes.
              </li>
              <li>
                <b>True Sage</b> maps 30+ habitat types and is our most accurate survey product.
              </li>
              <li>
                <b>True Oracle</b> is a bespoke solution tailored to your project’s specific needs and is
                handled by our expert team.
              </li>
            </ul>
          </Typography>
        }
      />
      <FaqQuestion
        question="How can I export the survey data?"
        component={
          <Typography sx={{ color: 'var(--gentian-black)' }} align="left">
            <ul>
              <li>
                <b>True Sight</b>: You can download a one-page PDF summary of your results. GIS files are not
                included by default—please contact us if you need them.
              </li>
              <li>
                <b>True Sage</b> and <b>True Oracle</b>: Both the full report and GIS files are available for
                download directly from the platform.
              </li>
            </ul>
          </Typography>
        }
      />
      <FaqQuestion
        question="Can I share the results of the survey?"
        answer="Yes, each survey has a unique share link. Recipients will need to have a platform account—or will be prompted to create one to access the project.<br/>For True Sage and True Oracle, you have additional control over what is shared. You can choose to share just the visual results, or also include access to downloadable files such as the full report and GIS data."
      />
      <FaqQuestion
        question="Can I rename a project?"
        answer="Yes, you can rename a project at any time on the platform. However, please note that the new name will appear only on the platform—it won’t update the name for the downloadable material as this has already been generated."
      />
      <FaqQuestion
        question="Why don’t the habitats line up exactly with the map imagery on the platform?"
        answer="That’s a great question. The satellite base map you see on the platform is often out of date and may not reflect current site conditions. We don’t rely on that imagery—instead, our habitat classification is based on a cache of very high-resolution aerial imagery which is less than three years old. This can sometimes lead to small visual mismatches, but the data we use for mapping is generally more current and detailed than what’s displayed as a background layer."
      />
      <FaqQuestion
        question="Can you provide the date of the imagery used for the habitat survey?"
        answer="We’re currently working on adding imagery date information to each survey so you can see exactly when the imagery was captured. This feature isn’t available just yet, but it’s on our roadmap and coming soon. In the meantime, our imagery is selected from a cache of aerial imagery, which is less than three years old."
      />

      <Typography variant="h3" component="h2" gutterBottom sx={{ paddingBottom: 2, paddingTop: 4 }} id="BNG">
        Biodiversity Net Gain (BNG) & Habitat Metrics
      </Typography>
      <FaqQuestion
        question="How are Biodiversity Net Gain (BNG) units calculated?"
        component={
          <Typography sx={{ color: 'var(--gentian-black)' }} align="left">
            We calculate BNG units by analysing the area you’ve selected using high-resolution imagery and our
            custom-built AI models. These models classify all land within your boundary into different habitat
            types, based on the Statutory Biodiversity Metric (SBM)—the official system used in the UK to
            support Biodiversity Net Gain (BNG). <br />
            <br />
            Here’s how it works in simple terms:
            <ol>
              <li>
                <b>Classifying habitats</b>: We identify and map the habitat types within your site.
              </li>
              <li>
                <b>Applying the SBM rules</b>: The SBM gives each habitat a &quot;distinctiveness&quot; score,
                and biodiversity units are calculated based on the area of each habitat multiplied by this
                score.
              </li>
              <li>
                <b>Estimating condition</b>: Normally, assessing habitat &quot;condition&quot; requires a site
                visit. Since this is a remote survey, we assume a moderate condition—the same assumption used
                in the government’s Small Sites Metric when field surveys aren’t possible.
              </li>
              <li>
                <b>Unit types</b>: We calculate separate biodiversity unit totals for habitats, hedgerows, and
                watercourses, as required by the SBM.
              </li>
              <li>
                <b>Strategic significance</b>: The SBM also includes a &quot;strategic significance&quot;
                score based on local conservation priorities. Since many local nature recovery strategies
                haven’t been released yet, this part isn’t factored into our current calculations.
              </li>
            </ol>
          </Typography>
        }
      />
      <FaqQuestion
        question="What are your habitat classifications based on?"
        component={
          <Typography sx={{ color: 'var(--gentian-black)' }} align="left">
            All habitats in the UK are classified using the UKHab classification system, which is the standard
            required by the Biodiversity Net Gain (BNG) metric. <br />
            <br />
            For sites outside the UK:
            <ul>
              <li>In Europe, we use the EUNIS habitat classification system.</li>
              <li>For global surveys, we use the IUCN classification, unless otherwise specified.</li>
            </ul>
          </Typography>
        }
      />
      <FaqQuestion
        question="What is 'distinctiveness'?"
        answer="Distinctiveness is a measure of how valuable or rare a habitat is for biodiversity. In the context of Biodiversity Net Gain (BNG), it helps determine how many biodiversity units a habitat is worth.<br/>Each habitat type is assigned a distinctiveness level—low, medium, high, or very high—based on its ecological importance. The rarer or more valuable the habitat, the higher its distinctiveness, and the more units it contributes per square metre."
      />
      <FaqQuestion
        question="What counts as a hedgerow unit?"
        answer="A hedgerow unit is a specific type of biodiversity unit used in Biodiversity Net Gain (BNG) assessments to measure the ecological value of linear hedgerows. Unlike most habitats, which are measured by area (hectares), hedgerows are measured by length (metres). Hedgerows are assessed separately from area-based habitats in the BNG metric because of their ecological importance, especially for species movement and connectivity. A hedgerow is defined as any line of woody species, shrubs or small trees that is at least 20 metres long and less than 5 metres wide at its base."
      />
      <FaqQuestion
        question="What counts as a watercourse unit?"
        answer="A watercourse unit is a specific type of biodiversity unit used in Biodiversity Net Gain (BNG) assessments to measure the ecological value of linear watercourses. Unlike most habitats, which are measured by area (hectares), watercourses are measured by length (metres). Watercourses are assessed separately from area-based habitats in the BNG metric because of their ecological importance, especially for aquatic species and connectivity. A watercourse is defined as any flowing water feature, including rivers, streams and canals that is at least 20 metres long."
      />
      <FaqQuestion
        question="Why aren’t hedgerows or watercourses showing up on my site?"
        answer="If hedgerows or watercourses aren’t appearing in your results, it’s likely because there aren’t any present within the boundary you provided—or they don’t meet the criteria for classification under the Biodiversity Net Gain (BNG) metric (e.g. minimum length or structure requirements)."
      />

      <Typography variant="h3" component="h2" gutterBottom sx={{ paddingBottom: 2, paddingTop: 4 }} id="Help">
        Help & Support
      </Typography>
      <FaqQuestion
        question="Why isn’t my platform login working?"
        answer="If you're having trouble logging in, it might be because of the sign-in method you used when you first created your account.<br/>If you signed up using Google, you’ll need to click ‘Continue with Google’ every time you log in. Typing in your Google email and password manually won’t work, because the system won’t recognise it as the same account.<br/>If you're still having issues, try resetting your password or contact our support team for help."
      />
      <FaqQuestion
        question="I tried to submit an area to survey and received the following message:"
        component={
          <React.Fragment>
            <Typography sx={{ color: 'var(--gentian-black)' }} align="center">
              ⚠️ <b>Oops! This polygon is self-intersecting</b> <br />
              Some edges of this polygon overlap, which may affect your results. Please adjust it before
              continuing.
            </Typography>
            <br />
            <Typography sx={{ color: 'var(--gentian-black)' }} align="left">
              A self-intersecting polygon is a shape where the edges cross over themselves, creating an
              overlap or intersection within the boundaries of the shape. It might look something like this:
            </Typography>
            <img
              src={intersectingImage}
              alt=""
              style={{ marginRight: '10px', height: '120px', width: '280px' }}
            />
            <Typography sx={{ color: 'var(--gentian-black)' }} align="left">
              Self-intersecting polygons are invalid for survey processing and analysis. You can fix this
              issue by:
              <br />
              <ol>
                <li>
                  <b>Editing the polygon</b>: Manually adjust the shape to ensure the edges do not cross each
                  other.
                </li>
                <li>
                  <b>Using a GIS tool</b>: Many GIS programs (e.g. QGIS) can automatically detect and repair
                  self-intersecting polygons by adjusting the geometry.
                </li>
                <li>
                  <b>Simplifying the shape</b>: If the polygon is too complex, simplifying the geometry might
                  help remove the self-intersections.
                </li>
              </ol>
            </Typography>

            <Typography sx={{ color: 'var(--gentian-black)' }} align="center" paddingTop={4}>
              ⚠️ <b>Multiple polygons detected</b> <br />
              We’ve detected more than one site. While True Sight is only available for a single area, you can
              continue with True Sage and True Oracle.
              <br /> Need True Sight? Just select True Sage and add ‘True Sight requested’ in the comment
              box—we’ll take a look!
            </Typography>
            <br />
            <Typography sx={{ color: 'var(--gentian-black)' }} align="left">
              True Sight is currently limited to single polygons only, as the results and statistics are
              generated per site. If you have multiple areas you’d like surveyed, you have two options:
              <ul>
                <li>Submit each polygon separately as individual True Sight requests</li>
                <li>
                  Or, select <b>True Sage</b> and write <i>“True Sight requested”</i> in the comment box —
                  we’ll review the request and get in touch.{' '}
                </li>
              </ul>
            </Typography>
            <Typography sx={{ color: 'var(--gentian-black)' }} align="center" paddingTop={4}>
              ⚠️ <b>Almost there!</b> <br />
              Processing is still underway for your previous request. Please wait for it to finish before
              proceeding. You can check the progress status in the meantime. Thanks for your patience!
            </Typography>
            <br />
            <Typography sx={{ color: 'var(--gentian-black)' }} align="left">
              You can only submit a new True Sight order once the previous one has finished processing. Please
              wait until the current request is complete before submitting another. In the meantime, you can
              check the progress status from your dashboard. Thanks for your patience — we’ll get your results
              to you as quickly as possible!
            </Typography>
          </React.Fragment>
        }
      />
      <FaqQuestion
        question="My True Sight payment failed. What should I do?"
        component={
          <Typography sx={{ color: 'var(--gentian-black)' }} align="left">
            If your True Sight payment didn’t go through, try the following: <br />
            <ol>
              <li>
                <b>Double-check your payment details</b>: Ensure your payment method is up to date and has
                sufficient funds.
              </li>
              <li>
                <b>Retry the payment</b>: You can attempt the payment again through the platform.
              </li>
              <li>
                <b>Use an alternative method</b>: If the issue persists, try submitting your request as
                <b> True Sage</b>, and we’ll be in touch with you as soon as possible to help.
              </li>
            </ol>
            If you continue having trouble, feel free to contact us for assistance.
          </Typography>
        }
      />
      <FaqQuestion
        question="The payment went through but the order failed—what now?"
        answer="If your payment was successfully processed but your order didn’t complete, please contact us at sales@gentian.io right away. We’ll investigate the issue and make sure everything is sorted out for you."
      />
      <FaqQuestion
        question="How do I delete my account?"
        answer="If you’d like to delete your account, please contact us at sales@gentian.io directly. Our support team will assist you with the process."
      />
      <FaqQuestion
        question="Is there support available if I need help?"
        answer="Yes, absolutely! You can reach our support team by emailing us at sales@gentian.io.<br/>We aim to respond to all inquiries within 1 business day."
      />
    </Container>
  );
};

export default FaqPage;
